import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Heading,
  IconButton,
  SimpleGrid,
  Badge,
  Divider,
} from '@chakra-ui/react';
import {
  FaTimes,
  FaPlay,
  FaPause,
  FaRedo,
  FaMusic,
  FaArrowRight,
} from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Database delle canzoni con file MIDI
// Ogni canzone ha: id, nome, midiFile (path al file), opzioni (4 opzioni multiple choice)
const SONGS_DATABASE = [
  {
    id: 1,
    name: 'Bohemian Rhapsody',
    midiFile: '/midi/bohemian-rhapsody.mid',
    options: [
      'Bohemian Rhapsody',
      'Stairway to Heaven',
      'Hotel California',
      "Sweet Child O' Mine",
    ],
  },
  {
    id: 2,
    name: 'Stairway to Heaven',
    midiFile: '/midi/stairway-to-heaven.mid',
    options: [
      'Stairway to Heaven',
      'Bohemian Rhapsody',
      'Hotel California',
      "Sweet Child O' Mine",
    ],
  },
  {
    id: 3,
    name: 'Hotel California',
    midiFile: '/midi/hotel-california.mid',
    options: [
      'Hotel California',
      'Bohemian Rhapsody',
      'Stairway to Heaven',
      "Sweet Child O' Mine",
    ],
  },
  // Aggiungi altre canzoni qui
];

/**
 * MidiGuessGame
 *
 * Gioco dove il giocatore ascolta una traccia MIDI e deve indovinare la canzone
 * scegliendo tra 4 opzioni multiple choice.
 */
function MidiGuessGame({ isActive = false, onGameEnd, onClose }) {
  const { isAuthenticated, userData } = useAuth();

  const [currentRound, setCurrentRound] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showInstructionsModal, setShowInstructionsModal] = useState(true);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [availableSongs, setAvailableSongs] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [options, setOptions] = useState([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [maxPlays] = useState(3); // Numero massimo di volte che si può riprodurre la traccia

  const audioRef = useRef(null);

  // Reset del gioco quando viene attivato
  useEffect(() => {
    if (isActive && showInstructionsModal) {
      setCurrentRound(0);
      setScore(0);
      setSelectedAnswer(null);
      setIsCorrect(null);
      setShowGameOverModal(false);
      setGameHistory([]);
      setHasAnswered(false);
      setPlayCount(0);
      setIsPlaying(false);

      // Mescola le canzoni disponibili
      const shuffled = [...SONGS_DATABASE].sort(() => Math.random() - 0.5);
      setAvailableSongs(shuffled);
    } else if (!isActive) {
      // Reset quando il gioco viene disattivato
      setShowInstructionsModal(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    }
  }, [isActive, showInstructionsModal]);

  // Carica la canzone corrente quando cambia il round
  useEffect(() => {
    if (currentRound < availableSongs.length && availableSongs.length > 0) {
      const song = availableSongs[currentRound];
      setCurrentSong(song);

      // Mescola le opzioni
      const shuffledOptions = [...song.options].sort(() => Math.random() - 0.5);
      setOptions(shuffledOptions);

      // Reset dello stato del round
      setSelectedAnswer(null);
      setIsCorrect(null);
      setHasAnswered(false);
      setPlayCount(0);
      setIsPlaying(false);

      // Ferma qualsiasi audio in riproduzione
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    } else if (
      currentRound >= availableSongs.length &&
      availableSongs.length > 0
    ) {
      // Fine del gioco
      setShowGameOverModal(true);
    }
  }, [currentRound, availableSongs]);

  // Funzione per riprodurre il MIDI
  // TODO: Implementare la riproduzione MIDI reale usando una libreria come:
  // - tone.js: npm install tone
  // - midi-sounds-react: npm install midi-sounds-react
  // - js-synthesizer: npm install js-synthesizer
  //
  // Esempio con tone.js:
  // import * as Tone from 'tone';
  // const synth = new Tone.Synth().toDestination();
  // const midi = await Midi.fromUrl(currentSong.midiFile);
  // const now = Tone.now();
  // midi.tracks.forEach(track => {
  //   track.notes.forEach(note => {
  //     synth.triggerAttackRelease(note.name, note.duration, note.time + now, note.velocity);
  //   });
  // });
  const playMidi = useCallback(() => {
    if (!currentSong || hasAnswered || playCount >= maxPlays) return;

    // Placeholder: mostra un messaggio che il MIDI verrà riprodotto
    // In futuro, implementa la riproduzione MIDI vera e propria usando una delle librerie sopra
    setIsPlaying(true);
    setPlayCount((prev) => prev + 1);

    // Simula la riproduzione (sostituisci con riproduzione MIDI reale)
    // Per ora usa un timeout, ma in produzione userai una libreria MIDI
    setTimeout(() => {
      setIsPlaying(false);
    }, 10000); // 10 secondi di riproduzione simulata
  }, [currentSong, hasAnswered, playCount, maxPlays]);

  // Gestisce la selezione di una risposta
  const handleAnswerSelect = (answer) => {
    if (hasAnswered) return;

    setSelectedAnswer(answer);
    const correct = answer === currentSong.name;
    setIsCorrect(correct);
    setHasAnswered(true);

    if (correct) {
      setScore((prev) => prev + 1);
    }

    // Aggiungi al history
    setGameHistory((prev) => [
      ...prev,
      {
        round: currentRound + 1,
        song: currentSong.name,
        selected: answer,
        correct,
      },
    ]);

    // Ferma la riproduzione
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  // Passa al round successivo
  const handleNextRound = () => {
    if (currentRound < availableSongs.length - 1) {
      setCurrentRound((prev) => prev + 1);
    } else {
      setShowGameOverModal(true);
    }
  };

  // Avvia il gioco dal modal delle istruzioni
  const handleStartGame = () => {
    setShowInstructionsModal(false);
    if (onGameEnd) {
      onGameEnd({ score: 0 });
    }
  };

  // Reset del gioco
  const handleRestart = () => {
    setCurrentRound(0);
    setScore(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setShowGameOverModal(false);
    setGameHistory([]);
    setHasAnswered(false);
    setPlayCount(0);
    setIsPlaying(false);

    // Mescola di nuovo le canzoni
    const shuffled = [...SONGS_DATABASE].sort(() => Math.random() - 0.5);
    setAvailableSongs(shuffled);
  };

  // Funzione helper per chiudere il gioco con logging
  const handleCloseWithLogging = useCallback(async () => {
    // Chiudi tutti i modal
    setShowInstructionsModal(false);
    setShowGameOverModal(false);

    // Ferma la riproduzione
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);

    // Logga il risultato se il gioco è stato avviato
    if (isAuthenticated && userData && gameHistory.length > 0) {
      try {
        const firstSocio = userData.tesseramenti?.[0];
        if (firstSocio) {
          const nomeSocio = firstSocio.nomeSocio || 'Utente';
          const socioDocumentId = firstSocio.documentId;

          await axios.post(
            '/api/game/activity-logs',
            {
              action: 'Nuovo punteggio Indovina la Canzone',
              description: `${nomeSocio} ha totalizzato ${score}/${availableSongs.length} punti in Indovina la Canzone`,
              category: 'game',
              socio: socioDocumentId,
              metadata: {
                score,
                totalRounds: availableSongs.length,
                gameHistory,
              },
            },
            {
              withCredentials: true,
            }
          );
        }
      } catch (error) {
        console.error('Errore nel logging del risultato del gioco:', error);
      }
    }

    // Reset dello stato
    setScore(0);
    setCurrentRound(0);
    setGameHistory([]);

    // Chiudi il gioco
    if (onClose) {
      onClose();
    }
  }, [
    isAuthenticated,
    userData,
    gameHistory,
    score,
    availableSongs.length,
    onClose,
  ]);

  // Se isActive è false e non c'è onClose, non renderizzare
  if (!isActive && !onClose) {
    return null;
  }

  return (
    <>
      <Box
        position="fixed"
        zIndex={9999}
        w="100%"
        h="100vh"
        overflow="auto"
        pointerEvents="auto"
        top={0}
        left={0}
        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      >
        {/* Header con punteggio e round */}
        {!showInstructionsModal && !showGameOverModal && (
          <Box
            position="absolute"
            top={20}
            left="50%"
            transform="translateX(-50%)"
            bg="rgba(0, 0, 0, 0.8)"
            color="white"
            px={6}
            py={3}
            borderRadius="md"
            zIndex={10000}
            border="2px solid"
            borderColor="#ffcc00"
          >
            <HStack spacing={4}>
              <VStack spacing={0} align="start">
                <Text fontSize="xs" color="gray.400">
                  Round
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="#ffcc00">
                  {currentRound + 1}/{availableSongs.length}
                </Text>
              </VStack>
              <Divider orientation="vertical" h="28px" />
              <VStack spacing={0} align="start">
                <Text fontSize="xs" color="gray.400">
                  Punteggio
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="#ffcc00">
                  {score}/{availableSongs.length}
                </Text>
              </VStack>
              {onClose && (
                <>
                  <Divider orientation="vertical" h="28px" />
                  <IconButton
                    aria-label="Chiudi gioco"
                    icon={<FaTimes />}
                    onClick={handleCloseWithLogging}
                    bg="transparent"
                    color="#ffcc00"
                    size="sm"
                    _hover={{ bg: 'rgba(255, 204, 0, 0.2)', color: 'white' }}
                  />
                </>
              )}
            </HStack>
          </Box>
        )}

        {/* Area di gioco principale */}
        {!showInstructionsModal && !showGameOverModal && currentSong && (
          <Box
            position="absolute"
            top="55%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="90%"
            maxW="600px"
            bg="rgba(255, 255, 255, 0.95)"
            borderRadius="lg"
            p={8}
            boxShadow="xl"
            mt={8}
          >
            <VStack spacing={6}>
              {/* Controlli audio */}
              <Box w="100%">
                <HStack justify="center" spacing={4}>
                  <Button
                    leftIcon={isPlaying ? <FaPause /> : <FaPlay />}
                    onClick={playMidi}
                    isDisabled={hasAnswered || playCount >= maxPlays}
                    colorScheme="blue"
                    size="lg"
                  >
                    {isPlaying ? 'Pausa' : 'Riproduci Traccia'}
                  </Button>
                  {playCount > 0 && (
                    <Badge colorScheme="gray" fontSize="md" px={3} py={1}>
                      {playCount}/{maxPlays} riproduzioni
                    </Badge>
                  )}
                </HStack>
                {playCount >= maxPlays && !hasAnswered && (
                  <Text
                    fontSize="sm"
                    color="orange.500"
                    textAlign="center"
                    mt={2}
                  >
                    Hai raggiunto il limite di riproduzioni. Scegli una
                    risposta!
                  </Text>
                )}
              </Box>

              {/* Domanda */}
              <Heading size="lg" textAlign="center" color="gray.800">
                Quale canzone stai ascoltando?
              </Heading>

              {/* Opzioni multiple choice */}
              <SimpleGrid columns={1} spacing={3} w="100%">
                {options.map((option) => {
                  let buttonColorScheme = 'gray';
                  const isDisabled = hasAnswered;

                  if (hasAnswered) {
                    if (option === currentSong.name) {
                      buttonColorScheme = 'green';
                    } else if (
                      option === selectedAnswer &&
                      option !== currentSong.name
                    ) {
                      buttonColorScheme = 'red';
                    }
                  }

                  return (
                    <Button
                      key={option}
                      onClick={() => handleAnswerSelect(option)}
                      isDisabled={isDisabled}
                      colorScheme={buttonColorScheme}
                      size="lg"
                      h="60px"
                      fontSize="lg"
                      fontWeight="semibold"
                      _hover={!isDisabled ? { transform: 'scale(1.02)' } : {}}
                    >
                      {option}
                    </Button>
                  );
                })}
              </SimpleGrid>

              {/* Messaggio risultato */}
              {hasAnswered && (
                <Box
                  w="100%"
                  p={4}
                  bg={isCorrect ? 'green.100' : 'red.100'}
                  borderRadius="md"
                  border="2px solid"
                  borderColor={isCorrect ? 'green.500' : 'red.500'}
                >
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={isCorrect ? 'green.700' : 'red.700'}
                    textAlign="center"
                  >
                    {isCorrect
                      ? '✅ Corretto!'
                      : `❌ Sbagliato! La risposta corretta era: ${currentSong.name}`}
                  </Text>
                </Box>
              )}

              {/* Bottone prossimo round */}
              {hasAnswered && (
                <Button
                  onClick={handleNextRound}
                  colorScheme="yellow"
                  size="lg"
                  w="100%"
                  leftIcon={<FaArrowRight />}
                >
                  {currentRound < availableSongs.length - 1
                    ? 'Prossima Canzone'
                    : 'Vedi Risultati'}
                </Button>
              )}
            </VStack>
          </Box>
        )}
      </Box>

      {/* Modal delle istruzioni iniziali */}
      <Modal
        isOpen={showInstructionsModal}
        onClose={() => {}}
        isCentered
        size="md"
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="#1a1d23" color="white" borderRadius="lg">
          <ModalHeader>
            <HStack justify="space-between" align="center">
              <Box flex={1} />
              <VStack flex={1} spacing={1} align="center">
                <Heading size="lg" color="#ffcc00" textAlign="center">
                  Indovina la Canzone
                </Heading>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  Gioco Musicale
                </Text>
              </VStack>
              <Box flex={1} display="flex" justifyContent="flex-end">
                {onClose && (
                  <IconButton
                    aria-label="Chiudi gioco"
                    icon={<FaTimes />}
                    onClick={handleCloseWithLogging}
                    bg="transparent"
                    color="gray.400"
                    size="sm"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.1)', color: 'white' }}
                    variant="ghost"
                  />
                )}
              </Box>
            </HStack>
          </ModalHeader>
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <Box
                bg="rgba(255, 204, 0, 0.1)"
                p={4}
                borderRadius="md"
                border="2px solid"
                borderColor="#ffcc00"
              >
                <Text
                  fontSize="lg"
                  color="#ffcc00"
                  textAlign="center"
                  fontWeight="bold"
                  mb={2}
                >
                  🎵 Testa le tue conoscenze musicali!
                </Text>
                <Text fontSize="md" color="white" textAlign="center">
                  Ascolta la traccia MIDI e indovina quale canzone stai
                  ascoltando scegliendo tra 4 opzioni.
                </Text>
              </Box>

              <Text fontSize="md" color="gray.300" textAlign="center">
                • Puoi riprodurre la traccia fino a 3 volte
                <br />
                • Hai 4 opzioni tra cui scegliere
                <br />
                • Ogni risposta corretta ti fa guadagnare 1 punto
                <br />• Il gioco finisce quando hai completato tutte le canzoni
              </Text>

              {!isAuthenticated && (
                <Box
                  mt={4}
                  p={3}
                  bg="rgba(255, 204, 0, 0.1)"
                  border="1px solid"
                  borderColor="#ffcc00"
                  borderRadius="md"
                >
                  <Text
                    fontSize="sm"
                    color="#ffcc00"
                    textAlign="center"
                    fontWeight="semibold"
                  >
                    💡 Accedi per registrare i tuoi punteggi e competere con gli
                    altri giocatori!
                  </Text>
                </Box>
              )}

              <Divider />

              <Button
                bg="#ffcc00"
                color="black"
                size="lg"
                onClick={handleStartGame}
                fontWeight="bold"
                fontSize="xl"
                py={6}
                _hover={{ bg: '#e6b800' }}
                leftIcon={<FaMusic />}
              >
                INIZIA A GIOCARE
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal di fine gioco */}
      <Modal
        isOpen={showGameOverModal}
        onClose={() => {}}
        isCentered
        size="md"
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="#1a1d23" color="white" borderRadius="lg">
          <ModalHeader>
            <Heading size="lg" color="#ffcc00">
              Gioco Completato!
            </Heading>
          </ModalHeader>
          <ModalCloseButton onClick={handleCloseWithLogging} />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {/* Risultato finale */}
              <Box textAlign="center" py={4} bg="#25282d" borderRadius="md">
                <Text fontSize="sm" color="gray.400" mb={2}>
                  Punteggio Finale
                </Text>
                <Text fontSize="4xl" fontWeight="bold" color="#ffcc00">
                  {score}/{availableSongs.length}
                </Text>
                <Text fontSize="md" color="gray.300" mt={2}>
                  {score === availableSongs.length
                    ? '🎉 Perfetto! Hai indovinato tutte le canzoni!'
                    : score >= availableSongs.length * 0.7
                      ? '👏 Ottimo risultato!'
                      : '👍 Continua a giocare per migliorare!'}
                </Text>
              </Box>

              <Divider />

              {/* Dettaglio risposte */}
              {gameHistory.length > 0 && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    mb={4}
                    color="gray.300"
                  >
                    Dettaglio Risposte:
                  </Text>
                  <VStack
                    spacing={2}
                    align="stretch"
                    maxH="300px"
                    overflowY="auto"
                  >
                    {gameHistory.map((item) => (
                      <HStack
                        key={`round-${item.round}-${item.song}`}
                        justify="space-between"
                        p={3}
                        bg="#25282d"
                        borderRadius="md"
                      >
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="bold" color="white">
                            Round {item.round}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {item.song}
                          </Text>
                        </VStack>
                        <Badge
                          colorScheme={item.correct ? 'green' : 'red'}
                          fontSize="sm"
                          px={3}
                          py={1}
                        >
                          {item.correct ? '✅' : '❌'}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}

              <Divider />

              <VStack spacing={4} w="100%">
                <Button
                  bg="#ffcc00"
                  color="black"
                  size="lg"
                  flex={1}
                  onClick={handleRestart}
                  fontWeight="bold"
                  leftIcon={<FaRedo />}
                  _hover={{ bg: '#e6b800' }}
                >
                  Gioca Ancora
                </Button>
                {onClose && (
                  <Button
                    variant="outline"
                    borderColor="gray.400"
                    color="gray.200"
                    size="lg"
                    flex={1}
                    onClick={handleCloseWithLogging}
                    fontWeight="bold"
                    _hover={{
                      bg: 'gray.700',
                      borderColor: 'gray.300',
                      color: 'white',
                    }}
                  >
                    Chiudi
                  </Button>
                )}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default MidiGuessGame;
